import React, { useCallback, useMemo, useRef, useState } from 'react';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_UNIT_UPDATE } from '@shared/constants';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import {
  getSelectedElementQuantityProperty,
  isElementQuantityName,
  isElementQuantityProperty,
} from '@shared/helpers/element-quantity.helpers';
import { useIconQuantityProperties } from '@client/hooks/use-quantity-properties.hooks';
import { Icon, isQuantityIconName } from '@ui/Icon/Icon';
import { ElementQuantityName } from '@shared/models/element-quantities.model';
import { IElement } from '@shared/models/project.model';
import ElementPropertyExpression from '@client/pages/MainView/components/SidePanel/components/ElementSidePanelContent/components/ElementCoreSettings/ElementPropertiesForm/ElementPropertyExpression';
import { Menu } from '@ui/menus/Menu';
import { labelFromValue } from '@shared/helpers/string.helpers';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import { sortBy } from 'lodash';

type ElementQuantityControlProps = {
  element: IElement;
  deactivated?: boolean;
};

export const ElementQuantityControl: React.FC<ElementQuantityControlProps> = ({
  element,
  deactivated = false,
}) => {
  const applyChanges = useApplyProjectChanges();
  const category = getElementCategory(element);
  const selectedQuantityProperty = getSelectedElementQuantityProperty(element);
  const iconQuantities = useIconQuantityProperties(element);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const unitMenuResolverRef = useRef<(() => void) | null>(null);

  const selectableQuantityOptions: {
    value: ElementQuantityName;
    icon?: React.ReactNode;
    selected?: boolean;
  }[] = useMemo(() => {
    if (
      !iconQuantities.length ||
      category?.disableQuantitySelection ||
      !isElementQuantityProperty(selectedQuantityProperty)
    ) {
      return [];
    }

    const options = iconQuantities.map(({ name }) => {
      const icon = isQuantityIconName(name) ? (
        <Icon category="quantity" icon={name} />
      ) : null;

      const selected = [
        selectedQuantityProperty.name,
        element.selectedQuantity,
      ].includes(name);

      return {
        value: name,
        icon,
        selected,
      };
    });
    return sortBy(options, 'icon');
  }, [
    iconQuantities,
    category?.disableQuantitySelection,
    selectedQuantityProperty,
    element.selectedQuantity,
  ]);
  const hasSelectableQuantityOptions = selectableQuantityOptions.length > 0;

  const resolveUnitMenuPromise = useCallback(() => {
    if (!unitMenuResolverRef.current) return;
    unitMenuResolverRef.current();
    unitMenuResolverRef.current = null;
  }, []);

  const handleUnitClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!hasSelectableQuantityOptions) return;
      setMenuAnchor(event.currentTarget);
      resolveUnitMenuPromise();
      return new Promise<void>((resolve) => {
        unitMenuResolverRef.current = resolve;
      });
    },
    [hasSelectableQuantityOptions, resolveUnitMenuPromise],
  );

  const handleUnitMenuClose = useCallback(() => {
    setMenuAnchor(null);
    resolveUnitMenuPromise();
  }, [resolveUnitMenuPromise]);

  const handleQuantityChange = useCallback(
    async (name: string) => {
      if (isElementQuantityName(name)) {
        await applyChanges([
          {
            type: ProjectChangeType.ElementQuantitySelect,
            elementId: element.id,
            quantityName: name,
          },
        ]);
        amplitudeLog(AMP_ELEMENT_UNIT_UPDATE, {
          ElementID: element.id,
          unit: name,
        });
      }
    },
    [applyChanges, element.id],
  );

  if (!selectedQuantityProperty) return null;

  return (
    <>
      <ElementPropertyExpression
        /* 
        Force remount on quantity switch to reset UnitNumberInput local state, 
        preventing stale values from bleeding into the new quantity (DEV-2886) 
        */
        key={selectedQuantityProperty.name}
        property={selectedQuantityProperty}
        element={element}
        deactivated={deactivated}
        active={hasSelectableQuantityOptions && menuAnchor !== null}
        onUnitClick={hasSelectableQuantityOptions ? handleUnitClick : undefined}
      />
      <Menu
        open={menuAnchor !== null}
        anchorEl={menuAnchor}
        onClose={handleUnitMenuClose}
      >
        {selectableQuantityOptions.map(({ value, icon, selected }) => (
          <Menu.Item
            selected={selected}
            key={value}
            icon={icon}
            onClick={() => void handleQuantityChange(value)}
          >
            {labelFromValue(value, true)}
          </Menu.Item>
        ))}
      </Menu>
    </>
  );
};
